import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { Writer as WriterType } from '@disco/state-management/api/writers'
import {
  useGetWriterByIdQuery,
  useSearchWritersQuery,
} from '@disco/state-management/api/writers'

import type { SearchableDropdownOption } from 'components/ds/SearchableDropdown'
import { SearchableDropdown } from 'components/ds/SearchableDropdown'
import { CreateNewAction } from 'components/ds/SearchableDropdown/CreateNewAction'
import { OptionWithEditIcon } from 'components/ds/SearchableDropdown/OptionWithEditIcon'

import { RIGHTS_DROPDOWN_WIDTH } from '../../constants'
import { formatIdentifiersForDisplay } from '../../utils/identifier-validation'

// Re-export Writer type for convenience
export type Writer = WriterType

export interface WriterDropdownProps {
  /** Current selected writer ID */
  value?: string
  /** Callback when a writer is selected */
  onSelect: (option: SearchableDropdownOption<Writer>) => void
  /** Placeholder text for the search input */
  placeholder?: string
  /** Whether the dropdown is disabled */
  disabled?: boolean
  /** Optional ARIA label for accessibility */
  'aria-label'?: string
  /** List of writer IDs to exclude from the results */
  exclusionList?: number[]
  /** Split ID to identify which split row triggered the creation */
  splitId?: string | number
  /** Splits key (track ID or BULK_EDIT_KEY) for updating the correct split after creation */
  splitsKey?: number | string
  /** Callback when the dropdown loses focus */
  onBlur?: () => void
}

/**
 * WriterDropdown - A specialized dropdown for selecting writers
 *
 * This component uses the SearchableDropdown component to provide a searchable
 * interface for selecting writers. It fetches writer data from the API and
 * displays each option with an edit icon.
 *
 * Features:
 * - Searchable writer selection
 * - Edit icon for each writer (links to edit route when implemented)
 * - "Create new writer" action button
 * - Exclusion list support (e.g., to prevent duplicate writers)
 *
 * @param props - Component props
 * @returns WriterDropdown component
 *
 * @example
 * ```tsx
 * <WriterDropdown
 *   value={selectedWriterId}
 *   onSelect={(option) => handleWriterSelect(option)}
 *   exclusionList={[1, 2, 3]}
 *   placeholder="Select writer..."
 * />
 * ```
 */
export function WriterDropdown({
  value,
  onSelect,
  placeholder = 'Writer',
  disabled = false,
  'aria-label': ariaLabel = 'Writer dropdown',
  exclusionList = [],
  splitId,
  splitsKey,
  onBlur,
}: WriterDropdownProps) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')

  // Use RTK Query to fetch and cache writers based on search
  const { data: searchWriters = [], isFetching: isSearchFetching } =
    useSearchWritersQuery({
      query: searchQuery,
      limit: 20,
      exclusionList,
    })

  // Fetch the selected writer by ID to ensure we have fresh data
  // Skip this query if there's no value or if we're actively searching
  // The cache should be populated by searchWriters for most cases
  const { data: selectedWriter } = useGetWriterByIdQuery(value ?? '', {
    skip: !value || !!searchQuery.trim(),
  })

  // Transform writers to dropdown options, ensuring selected writer is always included
  // Filter out excluded writers client-side (API returns unfiltered results for cache sharing)
  const options = useMemo(() => {
    const exclusionSet = new Set(exclusionList.map(id => id.toString()))

    const writerOptions = searchWriters
      .filter(writer => !exclusionSet.has(writer.id.toString()))
      .map(writer => ({
        id: writer.id.toString(),
        label: writer.name,
        data: writer,
      }))

    // Always include selected writer at the beginning if not in search results
    // This ensures the selected value is always available for highlighting
    if (
      selectedWriter &&
      !writerOptions.some(opt => opt.id === selectedWriter.id.toString())
    ) {
      writerOptions.unshift({
        id: selectedWriter.id.toString(),
        label: selectedWriter.name,
        data: selectedWriter,
      })
    }

    return writerOptions
  }, [searchWriters, selectedWriter, exclusionList])

  // Show loading when:
  // 1. Search is fetching (for dropdown content)
  // 2. Value is set but not found in options AND no active search (initial load)
  const isLoading =
    isSearchFetching ||
    Boolean(
      value && !searchQuery.trim() && !options.some(opt => opt.id === value),
    )

  // Handle search query changes
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  // Custom option renderer with edit icon
  const renderOption = useCallback(
    (option: SearchableDropdownOption<Writer>) => {
      const secondaryText = formatIdentifiersForDisplay(
        option.data?.external_identifiers,
      )
      return (
        <OptionWithEditIcon
          label={option.label}
          secondaryText={secondaryText}
          onEditClick={() => {
            // Navigate to edit writer modal and pass writer data via state
            // Include splitId and splitsKey for auto-selection after edit
            navigate(`rights-data/writers/${option.id}/edit`, {
              state: { writer: option.data, splitId, splitsKey },
            })
          }}
        />
      )
    },
    [navigate, splitId, splitsKey],
  )

  // "Create new writer" action component with relative path
  // Use function form to receive search value from SearchableDropdown
  const actionComponent = useCallback(
    (searchValue: string) => (
      <CreateNewAction
        label="Create new writer"
        to="rights-data/writers/new"
        searchText={searchValue}
        splitId={splitId}
        splitsKey={splitsKey}
      />
    ),
    [splitId, splitsKey],
  )

  return (
    <SearchableDropdown
      value={value}
      onSelect={onSelect}
      onSearchChange={handleSearchChange}
      options={options}
      loading={isLoading}
      placeholder={placeholder}
      renderOption={renderOption}
      actionComponent={actionComponent}
      aria-label={ariaLabel}
      disabled={disabled}
      maxHeight={208}
      onBlur={onBlur}
      customDropdownWidth={RIGHTS_DROPDOWN_WIDTH}
    />
  )
}

export default WriterDropdown
